import { formatDate } from '@frontend/utils';
import type {
  DialogFieldRow,
  EligibilityException,
  ExceptionBorrowerFinancials,
  ExceptionFieldId,
  ExceptionFieldInputs,
  ExceptionFieldErrors,
  ExceptionFieldValues,
  ExceptionProgramData,
  ExceptionValueFormat,
  LoanExceptionUpsertRequest,
} from '../types';
import {
  ADJUSTED_VALUE_RANGES,
  EXCEPTION_FIELDS,
  NA_DISPLAY,
  PRICE_ADJUSTMENT_MAX,
  PRICE_ADJUSTMENT_MIN,
} from './constants';

const toNumberOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

// Ratios from the API are decimals (0.97). Convert to a whole percent (97),
// preserving decimals (0.8888 -> 88.88) and stripping float noise. Null stays
// null (N/A).
const ratioToPercent = (value: unknown): number | null => {
  const num = toNumberOrNull(value);
  return num === null ? null : Number((num * 100).toFixed(4));
};

// DTI and Liquid Assets come back as 0/0.0 when the program has no guideline;
// treat that as "no guideline" so the row renders N/A and stays disabled.
const nonZeroOrNull = (value: number | null): number | null =>
  value === 0 ? null : value;

// Guideline from program.eligibilityDetails[0]; current from the loan payload
// (loanInformation) plus the program's priced ratios. Guideline stays null when
// the source has none (maxLine, DTI, Liquid Assets) so the row renders "N/A".
export const buildExceptionFieldValues = (
  program: ExceptionProgramData | null | undefined,
  loanInformation: Record<string, unknown> | null | undefined,
  borrower?: ExceptionBorrowerFinancials | null,
): ExceptionFieldValues[] => {
  const eligibility = program?.eligibilityDetails?.[0] ?? {};
  const loan = loanInformation ?? {};
  const { creditScore, liquidAsset } = borrower ?? {};
  const current = (value: unknown): number => toNumberOrNull(value) ?? 0;

  return [
    {
      exceptionFieldId: 1,
      guidelineValue: toNumberOrNull(eligibility.credit),
      currentValue: current(
        creditScore ?? loan.averageRepresentativeCreditScore,
      ),
    },
    {
      exceptionFieldId: 2,
      guidelineValue: ratioToPercent(eligibility.ltv),
      currentValue: ratioToPercent(program?.ltv) ?? 0,
    },
    {
      exceptionFieldId: 3,
      guidelineValue: ratioToPercent(eligibility.cltv),
      currentValue: ratioToPercent(program?.cltv) ?? 0,
    },
    {
      exceptionFieldId: 4,
      guidelineValue: ratioToPercent(eligibility.hcltv),
      currentValue: ratioToPercent(program?.hcltv) ?? 0,
    },
    {
      exceptionFieldId: 5,
      guidelineValue: toNumberOrNull(eligibility.maxLoan),
      currentValue: current(loan.totalMortgageAmount),
    },
    {
      exceptionFieldId: 6,
      guidelineValue: toNumberOrNull(eligibility.maxLine),
      currentValue: current(loan.helocCreditLimit),
    },
    {
      exceptionFieldId: 7,
      guidelineValue: toNumberOrNull(eligibility.maxCash),
      currentValue: current(loan.cashOut),
    },
    {
      exceptionFieldId: 8,
      guidelineValue: nonZeroOrNull(ratioToPercent(program?.minBRatio)),
      currentValue: ratioToPercent(program?.dtiBRatio) ?? 0,
    },
    {
      exceptionFieldId: 9,
      guidelineValue: nonZeroOrNull(toNumberOrNull(program?.cashToClose)),
      currentValue: current(liquidAsset),
    },
  ];
};

// Manage mode: anchor guideline + current to the values saved on the exception
// so the adjusted value stays stable on reopen instead of drifting.
export const mergeExceptionFieldValues = (
  base: ExceptionFieldValues[],
  existingException?: EligibilityException,
): ExceptionFieldValues[] => {
  if (!existingException) {
    return base;
  }
  const savedById = new Map(
    existingException.adjustments.map((adj) => [adj.exceptionFieldId, adj]),
  );
  return base.map((fv) => {
    const saved = savedById.get(fv.exceptionFieldId);
    if (!saved) {
      return fv;
    }
    return {
      ...fv,
      guidelineValue: saved.guidelineValue ?? fv.guidelineValue ?? null,
      currentValue: saved.loanValue ?? fv.currentValue,
    };
  });
};

export const resolveEnteredBy = (
  mode: 'apply' | 'manage',
  existingException: EligibilityException | undefined,
  officeUsers: Array<{ value?: number; label?: string }>,
): string | null => {
  if (mode !== 'manage') {
    return null;
  }
  const match = (officeUsers ?? []).find(
    (user) => user.value === existingException?.userId,
  );
  return match?.label ?? '';
};

// Manage mode shows the saved exception's date; apply mode (or a missing value)
// returns null so the caller can fall back to today.
export const resolveEnteredDate = (
  mode: 'apply' | 'manage',
  existingException?: EligibilityException,
): string | null =>
  mode === 'manage' && existingException?.lastUpdatedAt
    ? formatDate(existingException.lastUpdatedAt, 'Calendar')
    : null;

export const formatValue = (
  value: number | null,
  format: ExceptionValueFormat,
): string => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return NA_DISPLAY;
  }
  switch (format) {
    case 'integer':
      return Math.round(value).toString();
    case 'percent':
      return `${Number(value.toFixed(4))}%`;
    case 'currency':
      return `$${Number(value).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    default:
      return `${value}`;
  }
};

// Adjustment is a whole-number (int) delta applied to the current value.
// Decimal base values are preserved (e.g. 88.88 + (-1) = 87.88).
export const computeAdjustedValue = (
  currentValue: number,
  adjustment: number | null,
): number => {
  if (adjustment === null || Number.isNaN(adjustment)) {
    return currentValue;
  }
  return currentValue + adjustment;
};

// Builds the enriched dialog rows from the static guideline/current values,
// merging any previously-saved adjustments (manage mode / re-open).
export const buildDialogRows = (
  fieldValues: ExceptionFieldValues[],
  existingException?: EligibilityException,
): DialogFieldRow[] => {
  const valuesById = new Map(
    fieldValues.map((fv) => [fv.exceptionFieldId, fv]),
  );
  const savedById = new Map(
    (existingException?.adjustments ?? []).map((adj) => [
      adj.exceptionFieldId,
      adj,
    ]),
  );

  return EXCEPTION_FIELDS.map(({ exceptionFieldId, fieldName, format }) => {
    const values = valuesById.get(exceptionFieldId);
    const guidelineValue = values?.guidelineValue ?? null;
    const currentValue = values?.currentValue ?? 0;
    const saved = savedById.get(exceptionFieldId);
    const isDisabled = guidelineValue === null;
    const adjustment = isDisabled ? null : saved?.eligibilityAdjustment ?? null;

    return {
      exceptionFieldId,
      fieldName,
      format,
      guidelineValue,
      currentValue,
      adjustment,
      priceAdjustment: isDisabled ? null : saved?.priceAdjustment ?? null,
      isDisabled,
    };
  });
};

// Parses the raw adjustment input to a whole number, or null when empty/invalid.
export const parseIntegerInput = (raw: string): number | null => {
  if (raw === '' || raw === '-' || raw === '+') {
    return null;
  }
  const parsed = parseInt(raw, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

// Price adjustment to 3 decimals, 0 when empty. Out of range values are kept.
export const parsePriceInput = (raw: string): number => {
  if (raw === '' || raw === '-' || raw === '+' || raw === undefined) {
    return 0;
  }
  const parsed = Number(raw);
  if (Number.isNaN(parsed)) {
    return 0;
  }
  return Number(parsed.toFixed(3));
};

// Range check on save, limited to rows with a non-zero adjustment.
export const validateExceptionRanges = (
  rows: DialogFieldRow[],
  inputs: Partial<Record<ExceptionFieldId, ExceptionFieldInputs>>,
): Partial<Record<ExceptionFieldId, ExceptionFieldErrors>> => {
  const errors: Partial<Record<ExceptionFieldId, ExceptionFieldErrors>> = {};

  rows.forEach((row) => {
    const value = inputs[row.exceptionFieldId];
    const adjustment = parseIntegerInput(value?.adjustment ?? '');
    if (row.isDisabled || adjustment === null || adjustment === 0) {
      return;
    }

    const { min, max } = ADJUSTED_VALUE_RANGES[row.exceptionFieldId];
    const adjusted = computeAdjustedValue(row.currentValue, adjustment);
    const price = parsePriceInput(value?.priceAdjustment ?? '');
    const rowErrors: ExceptionFieldErrors = {};

    if (adjusted < min || (max !== null && adjusted > max)) {
      rowErrors.adjustedValue = true;
    }
    if (price < PRICE_ADJUSTMENT_MIN || price > PRICE_ADJUSTMENT_MAX) {
      rowErrors.priceAdjustment = true;
    }
    if (rowErrors.adjustedValue || rowErrors.priceAdjustment) {
      errors[row.exceptionFieldId] = rowErrors;
    }
  });

  return errors;
};

// Builds the upsert body from the dialog rows + inputs, keeping only fields with a non-zero adjustment; returns null when there is nothing to save.
export const buildLoanExceptionUpsertRequest = (
  rows: DialogFieldRow[],
  inputs: Partial<Record<ExceptionFieldId, ExceptionFieldInputs>>,
  comments: string,
): LoanExceptionUpsertRequest | null => {
  const adjustments = rows
    .map((row) => {
      const value = inputs[row.exceptionFieldId];
      const eligibilityAdjustment = parseIntegerInput(value?.adjustment ?? '');
      if (eligibilityAdjustment === null || eligibilityAdjustment === 0) {
        return null;
      }
      return {
        exceptionFieldId: row.exceptionFieldId,
        eligibilityAdjustment,
        priceAdjustment: parsePriceInput(value?.priceAdjustment ?? ''),
        loanValue: row.currentValue,
        guidelineValue: Math.round(row.guidelineValue ?? 0),
      };
    })
    .filter(Boolean) as LoanExceptionUpsertRequest['adjustments'];

  if (adjustments.length === 0) {
    return null;
  }

  const trimmedComments = comments.trim();
  return {
    comments: trimmedComments === '' ? null : trimmedComments,
    adjustments,
  };
};

// Reads the first present key, tolerating camelCase or PascalCase from the proxy passthrough.
const pickField = (source: any, keys: string[]): unknown => {
  if (!source) {
    return undefined;
  }
  for (const key of keys) {
    if (source[key] !== undefined && source[key] !== null) {
      return source[key];
    }
  }
  return undefined;
};

// Maps the GET response into an EligibilityException, or null when empty/absent. enteredBy is
// left blank so the dialog falls back to the current user (the response carries only userId).
export const mapLoanExceptionResponse = (
  raw: any,
): EligibilityException | null => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const body = raw.object ?? raw.Object ?? raw;
  const rawAdjustments = pickField(body, ['adjustments', 'Adjustments']);
  if (!Array.isArray(rawAdjustments)) {
    return null;
  }
  const adjustments = rawAdjustments.map((item) => ({
    exceptionFieldId: Number(
      pickField(item, [
        'exceptionFieldId',
        'exceptionFieldID',
        'ExceptionFieldID',
      ]),
    ) as ExceptionFieldId,
    eligibilityAdjustment: Number(
      pickField(item, ['eligibilityAdjustment', 'EligibilityAdjustment']) ?? 0,
    ),
    priceAdjustment: Number(
      pickField(item, ['priceAdjustment', 'PriceAdjustment']) ?? 0,
    ),
    loanValue: Number(pickField(item, ['loanValue', 'LoanValue']) ?? 0),
    guidelineValue: Number(
      pickField(item, ['guidelineValue', 'GuidelineValue']) ?? 0,
    ),
  }));
  const lastUpdatedAt = pickField(body, ['lastUpdatedAt', 'LastUpdatedAt']);
  return {
    loanExceptionId: Number(
      pickField(body, [
        'loanExceptionId',
        'loanExceptionID',
        'LoanExceptionId',
      ]) ?? 0,
    ),
    loanId: Number(pickField(body, ['loanId', 'loanID', 'LoanId']) ?? 0),
    programId: Number(
      pickField(body, ['programId', 'programID', 'ProgramId']) ?? 0,
    ),
    userId: Number(pickField(body, ['userId', 'userID', 'UserId']) ?? 0),
    enteredBy: '',
    lastUpdatedAt: lastUpdatedAt ? String(lastUpdatedAt).slice(0, 10) : '',
    comments: (pickField(body, ['comments', 'Comments']) as string) ?? null,
    adjustments,
  };
};

export const buildExceptionAdjustmentSummary = (
  exception: EligibilityException,
): string => {
  const fieldById = new Map(
    EXCEPTION_FIELDS.map((field) => [field.exceptionFieldId, field]),
  );
  return exception.adjustments
    .map((adj) => {
      const field = fieldById.get(adj.exceptionFieldId);
      if (!field) {
        return null;
      }
      const adjusted = computeAdjustedValue(
        adj.loanValue ?? 0,
        adj.eligibilityAdjustment,
      );
      return `${field.fieldName} Adjusted to ${formatValue(
        adjusted,
        field.format,
      )}`;
    })
    .filter(Boolean)
    .join(', ');
};
